import * as A from 'astronomy-engine';
import { DEG, Vec3, raDecToVec } from '../lib/math';

export interface Shower {
  code: string;
  name: string;
  lStart: number;
  lEnd: number;
  lPeak: number;
  zhr: number | null;
  variable: boolean;
  v: number;
  r: number;
  radiant: [number, number, number][]; // [solar longitude, RA°, Dec°]
  note: string;
}

export interface ShowerNow {
  shower: Shower;
  zhr: number; // at this moment
  ra: number;
  dec: number;
  dir: Vec3; // J2000 unit vector of the radiant
  daysToPeak: number; // negative after the peak
}

const EPS = 23.4392911 * DEG;

/** Apparent geocentric solar longitude referred to the J2000 ecliptic, degrees (the IMO convention). */
export function solarLongitude(date: Date): number {
  const v = A.GeoVector(A.Body.Sun, date, true);
  const y = v.y * Math.cos(EPS) + v.z * Math.sin(EPS);
  return (((Math.atan2(y, v.x) / DEG) % 360) + 360) % 360;
}

const rel = (l: number, from: number) => (((l - from) % 360) + 360) % 360;

/** Whether solar longitude `l` lies in the (possibly year-wrapping) activity window. */
export function inWindow(s: Shower, l: number): boolean {
  const span = rel(Math.max(s.lEnd, s.lPeak + 0.4), s.lStart) || 360;
  return rel(l, s.lStart) <= span;
}

/** Zenithal hourly rate at solar longitude `l`: exponential rise and fall reaching ~1–2 at the ends. */
export function zhrAt(s: Shower, l: number): number {
  if (!inWindow(s, l)) return 0;
  const max = s.zhr ?? 5;
  const before = rel(s.lPeak, s.lStart);
  const pos = rel(l, s.lStart);
  const d = pos <= before ? before - pos : pos - before;
  const edge = pos <= before ? Math.max(before, 0.5) : Math.max(rel(Math.max(s.lEnd, s.lPeak + 0.4), s.lPeak), 0.5);
  const floor = Math.min(1.5, max * 0.5);
  const B = Math.log10(max / floor) / edge;
  return max * Math.pow(10, -B * d);
}

/** Radiant position at solar longitude `l`, interpolated from the IMO drift table. */
export function radiantAt(s: Shower, l: number): { ra: number; dec: number } {
  const rows = s.radiant;
  if (rows.length === 1) return { ra: rows[0][1], dec: rows[0][2] };
  const x = rel(l, s.lStart);
  let i = 0;
  while (i < rows.length - 2 && rel(rows[i + 1][0], s.lStart) < x) i++;
  const [l0, ra0, d0] = rows[i];
  const [l1, ra1, d1] = rows[i + 1];
  const x0 = rel(l0, s.lStart), x1 = rel(l1, s.lStart);
  const t = x1 === x0 ? 0 : (x - x0) / (x1 - x0);
  let dra = ra1 - ra0;
  if (dra > 180) dra -= 360;
  if (dra < -180) dra += 360;
  return { ra: (((ra0 + dra * t) % 360) + 360) % 360, dec: d0 + (d1 - d0) * t };
}

/** Longitude difference to days (the Sun moves ~0.9856°/day). */
const lonToDays = (dl: number) => dl / 0.9856;

export function activeShowers(showers: Shower[], date: Date, minZhr = 0.8): ShowerNow[] {
  const l = solarLongitude(date);
  const out: ShowerNow[] = [];
  for (const s of showers) {
    const zhr = zhrAt(s, l);
    if (zhr < minZhr) continue;
    const { ra, dec } = radiantAt(s, l);
    let dl = s.lPeak - l;
    if (dl > 180) dl -= 360;
    if (dl < -180) dl += 360;
    out.push({ shower: s, zhr, ra, dec, dir: raDecToVec(ra, dec), daysToPeak: lonToDays(dl) });
  }
  return out.sort((a, b) => b.zhr - a.zhr);
}

/** Upcoming peaks within `days`, soonest first. */
export function upcomingPeaks(showers: Shower[], date: Date, days = 120): { shower: Shower; date: Date }[] {
  const l = solarLongitude(date);
  const out: { shower: Shower; date: Date }[] = [];
  for (const s of showers) {
    const dl = rel(s.lPeak, l);
    const approx = lonToDays(dl);
    if (approx > days) continue;
    // Refine the peak time with two Newton steps on solar longitude.
    let t = new Date(date.getTime() + approx * 86400e3);
    for (let k = 0; k < 3; k++) {
      let e = s.lPeak - solarLongitude(t);
      if (e > 180) e -= 360;
      if (e < -180) e += 360;
      t = new Date(t.getTime() + lonToDays(e) * 86400e3);
    }
    out.push({ shower: s, date: t });
  }
  return out.sort((a, b) => a.date.getTime() - b.date.getTime());
}

/**
 * Expected meteors per hour for one observer: the standard inverse of the ZHR correction,
 * HR = ZHR · sin(radiant altitude) · r^(Lm − 6.5) · (1 − cloud fraction).
 */
export function hourlyRate(zhr: number, radiantAltDeg: number, r: number, limitingMag: number, cloud = 0): number {
  if (radiantAltDeg <= 0) return 0;
  return zhr * Math.sin(radiantAltDeg * DEG) * Math.pow(r, Math.min(0, limitingMag - 6.5)) * (1 - cloud);
}

/**
 * Sporadic background: roughly 2–4 per hour in the evening rising to 8–12 before dawn under a
 * dark sky, following the altitude of the Earth's apex. The antihelion source is included.
 */
export function sporadicRate(apexAltDeg: number, limitingMag: number, cloud = 0): number {
  const base = 6 + 5 * Math.sin(Math.max(-60, Math.min(90, apexAltDeg)) * DEG);
  return Math.max(0.5, base) * Math.pow(3.0, Math.min(0, limitingMag - 6.5)) * (1 - cloud);
}

/** Direction of the Earth's way (apex), J2000: ecliptic longitude λ☉ − 90°, latitude 0. */
export function apexDir(date: Date): Vec3 {
  const l = (solarLongitude(date) - 90) * DEG;
  const x = Math.cos(l), y = Math.sin(l);
  return [x, y * Math.cos(EPS), y * Math.sin(EPS)];
}

/** Antihelion source radiant: about 12° east of the antisolar point on the ecliptic. */
export function antihelionDir(date: Date): Vec3 {
  const l = (solarLongitude(date) + 192) * DEG;
  const x = Math.cos(l), y = Math.sin(l);
  return [x, y * Math.cos(EPS), y * Math.sin(EPS)];
}
